import { useNavigate } from 'react-router-dom';
import {
  Baby,
  Bath,
  Bed,
  ChefHat,
  ChevronRight,
  DoorOpen,
  Package,
  Shirt,
  Sofa,
  Trees,
  type LucideIcon,
} from 'lucide-react';

import { FURNITURE_TYPES, FURNITURE_TYPE_LABELS_RU, type FurnitureType } from '@pomoshnik/shared';

import { BottomBar } from '@/components/BottomBar/BottomBar';
import { Header } from '@/components/Header/Header';

/**
 * Экран 2.6.1 — выбор типа мебели для конфигуратора (PRD §08 + ADR-0015 §Q5).
 *
 * Container + Presenter inline (ADR-0005 «split when meaningful» — здесь
 * нет meaningful complexity для split: один useNavigate hook, один уровень
 * рендеринга, один inline button-компонент).
 *
 * UI:
 *   - <Header title="Выбор типа мебели" /> (Bebas Neue 28px).
 *   - 9 type cards вертикально (P-019 vertical buttons), каждая c lucide
 *     иконкой + RU label.
 *   - <BottomBar variant='default' /> (3 кнопки P-023: Назад / На главную /
 *     Что улучшить).
 *
 * Click: `navigate('/configurator/:type')` — Step 4 регистрирует роут на
 * `ConfiguratorScreen` (Phase 1 stub, Phase 2 SVG-визуализация).
 *
 * VideoButton НЕ добавляем — Block 6 первый экран функции с конфигуратором
 * это 2.6.2 (см. CLAUDE.md §«MUST: на каждом экране-входе функции»);
 * Type Picker — wrapper выбора, не сам конфигуратор.
 *
 * ── ICON_MAP invariant ──────────────────────────────────────────────────
 * Local `ICON_MAP` сопоставляет `FurnitureType` → lucide-компонент. Имена
 * компонентов ОБЯЗАНЫ совпадать со строками в `FURNITURE_TYPE_ICONS` из
 * `packages/shared` (locked test'ами в `configurator.test.ts`).
 *
 * Test ниже (Step 3) проверяет что `ICON_MAP[type].displayName ===
 * FURNITURE_TYPE_ICONS[type]` — drift между shared vocab и UI ловится сразу.
 */
const ICON_MAP: Record<FurnitureType, LucideIcon> = {
  kitchen: ChefHat,
  hallway: DoorOpen,
  wardrobe: Shirt,
  bedroom: Bed,
  living_room: Sofa,
  children: Baby,
  bathroom: Bath,
  balcony: Trees,
  other: Package,
};

export function TypePickerScreen() {
  const navigate = useNavigate();

  return (
    <main className="min-h-screen flex flex-col bg-canvas">
      <Header title="Выбор типа мебели" />

      <div className="flex-1 flex flex-col gap-3 p-6">
        {FURNITURE_TYPES.map((type) => (
          <TypeCard key={type} type={type} onClick={() => navigate(`/configurator/${type}`)} />
        ))}
      </div>

      <BottomBar onFeedback={() => alert('TODO: Phase 7 — Блок 10 feedback modal')} />
    </main>
  );
}

interface TypeCardProps {
  type: FurnitureType;
  onClick: () => void;
}

function TypeCard({ type, onClick }: TypeCardProps) {
  const Icon = ICON_MAP[type];
  const label = FURNITURE_TYPE_LABELS_RU[type];

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Выбрать ${label} как тип мебели`}
      className={
        'w-full text-left rounded-xl p-4 flex flex-row gap-4 items-center min-h-tap-chip-lg ' +
        'bg-card border-[1.5px] border-accent shadow-card ' +
        'focus-visible:ring-2 focus-visible:ring-cta focus-visible:outline-none active:opacity-80 ' +
        'transition-opacity'
      }
    >
      <div
        aria-hidden="true"
        className={
          'w-16 h-16 rounded-xl flex items-center justify-center shrink-0 ' +
          'bg-card border-[1.5px] border-accent text-ink'
        }
      >
        <Icon size={32} aria-hidden="true" />
      </div>
      <div className="flex-1 min-w-0">
        <div
          className="font-bold text-ink leading-snug"
          style={{ letterSpacing: '-0.01em', fontSize: 20 }}
        >
          {label}
        </div>
      </div>
      <ChevronRight size={20} className="text-ghost shrink-0" aria-hidden="true" />
    </button>
  );
}

/**
 * Экспорт `ICON_MAP` для теста инварианта (displayName === FURNITURE_TYPE_ICONS).
 * Не для consumer'ов — используйте `<TypePickerScreen />` или импортируйте
 * иконки напрямую из `lucide-react`.
 *
 * Disable react-refresh правила: ICON_MAP — статическая константа, не реактивное
 * состояние, HMR при её изменении не обязан перерисовывать живой стейт. Вынос
 * в отдельный файл (`./typePickerIconMap.ts`) — over-engineering для одной
 * test-only константы; при росте до ≥3 экспортируемых non-component символов
 * будет смысл рефакторить (B-NNN если такое случится).
 */
// eslint-disable-next-line react-refresh/only-export-components
export const __INTERNAL_ICON_MAP = ICON_MAP;
